import * as THREE from 'three';
import { rippleNormals } from '../look/noise.js';
import { band, type Height } from './terrain.js';

/**
 * Water (ADR-032): one draw call, its own shader and no patching of
 * three's -- four sine waves with their exact normal, a scrolling ripple
 * normal for the detail, Schlick's fresnel between the deep colour and the
 * sky's, a glint of the sun, foam where it is shallow, and three's fog
 * chunks so it melts into the horizon like everything else.
 */

const vertexShader = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
uniform float uTime;
uniform vec4 uWaves[4];
uniform float uRise;
attribute float depth;
varying vec3 vWorld;
varying vec3 vNormal;
varying float vDepth;
void main() {
  vec3 world = (modelMatrix * vec4(position, 1.0)).xyz;
  float h = 0.0;
  vec2 grad = vec2(0.0);
  for (int i = 0; i < 4; i++) {
    vec2 d = normalize(uWaves[i].xy);
    float k = 6.2831853 / uWaves[i].w;
    float c = sqrt(9.8 / k);
    float f = k * (dot(d, world.xz) - c * uTime);
    h += uWaves[i].z * sin(f);
    grad += uWaves[i].z * k * cos(f) * d;
  }
  // Risen or fallen (a tide, a flood): deeper or shallower by as much.
  float deep = max(0.0, depth + uRise);
  // Calm where it is shallow, so the shore does not slosh.
  float calm = smoothstep(0.0, 2.5, deep);
  world.y += h * calm;
  vNormal = normalize(vec3(-grad.x * calm, 1.0, -grad.y * calm));
  vWorld = world;
  vDepth = deep;
  vec4 mvPosition = viewMatrix * vec4(world, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const fragmentShader = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform float uTime;
uniform vec3 uDeep;
uniform vec3 uShallow;
uniform vec3 uSky;
uniform vec3 uSunDirection;
uniform vec3 uSunColour;
uniform vec3 uFoam;
uniform float uFoamAmount;
uniform sampler2D uRipples;
varying vec3 vWorld;
varying vec3 vNormal;
varying float vDepth;
void main() {
  vec3 r1 = texture2D(uRipples, vWorld.xz * 0.11 + vec2(uTime * 0.021, uTime * 0.013)).xyz * 2.0 - 1.0;
  vec3 r2 = texture2D(uRipples, vWorld.xz * 0.027 - vec2(uTime * 0.012, -uTime * 0.017)).xyz * 2.0 - 1.0;
  vec3 n = normalize(vNormal + vec3(r1.x + r2.x, 0.0, r1.y + r2.y) * 0.45);
  vec3 v = normalize(cameraPosition - vWorld);
  float fresnel = 0.02 + 0.98 * pow(1.0 - max(dot(n, v), 0.0), 5.0);
  vec3 body = mix(uShallow, uDeep, max(smoothstep(0.0, 6.0, vDepth), smoothstep(70.0, 120.0, length(vWorld.xz))));
  // Never a mirror: a sea at a grazing angle still shows its own colour.
  vec3 colour = mix(body, uSky, fresnel * 0.7);
  float glint = pow(max(dot(reflect(-v, n), uSunDirection), 0.0), 220.0);
  colour += uSunColour * glint * 3.0;
  // Foam only on the near shore: far out the grid is coarse, and a depth
  // interpolated across one big triangle would draw a white sheet.
  float band = (1.0 - smoothstep(0.05, 0.7, vDepth)) * (1.0 - smoothstep(70.0, 120.0, length(vWorld.xz)));
  float lace = texture2D(uRipples, vWorld.xz * 0.35 + vec2(uTime * 0.04)).x;
  float foam = band * smoothstep(0.45, 0.7, lace + 0.3 * sin(uTime * 1.2 - vDepth * 5.0));
  colour = mix(colour, uFoam, clamp(foam, 0.0, 1.0) * 0.85 * uFoamAmount);
  gl_FragColor = vec4(colour, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

export type WaterOptions = {
  readonly level: number;
  /** The ground under the water, for its depth: shallow water is lighter and foams. */
  readonly bed: Height;
  readonly radius: number;
  readonly deep: THREE.ColorRepresentation;
  readonly shallow: THREE.ColorRepresentation;
  readonly sky: THREE.ColorRepresentation;
  readonly sunDirection: THREE.Vector3;
  readonly sunColour: THREE.ColorRepresentation;
  /** How high its waves are beside the sea's: 0.1 for a ditch or a sheltered lake (part 2). */
  readonly waves?: number;
  /** Foam where it is shallow: a sea's shore has it, a ditch does not. */
  readonly foam?: boolean;
  /** Its own shape, flat at `level` (a polder's ditches); a disc of `radius` otherwise. */
  readonly shape?: THREE.BufferGeometry;
};

export type Water = {
  readonly mesh: THREE.Mesh;
  /** Where its surface is now, at rest. */
  readonly level: number;
  update(elapsed: number): void;
  /** Risen or fallen to `level` (a tide, a flood): the whole sheet moves, deeper by as much. */
  setLevel(level: number): void;
};

export function buildWater(options: WaterOptions): Water {
  const geometry =
    options.shape ??
    band({
      inner: 0,
      outer: options.radius,
      rings: 90,
      segments: 128,
      bunch: 2.2,
      height: () => options.level,
      tile: 1,
    });
  if (geometry.getAttribute('color')) geometry.deleteAttribute('color');
  const position = geometry.getAttribute('position');
  const depth = new Float32Array(position.count);
  // Signed: ground above the water now is stored below zero, so water that rises
  // (setLevel) has its shore, its calm and its foam where it meets the ground.
  for (let i = 0; i < position.count; i++) {
    depth[i] = THREE.MathUtils.clamp(
      options.level - options.bed(position.getX(i), position.getZ(i)),
      -30,
      30,
    );
  }
  geometry.setAttribute('depth', new THREE.BufferAttribute(depth, 1));
  const linear = (c: THREE.ColorRepresentation): THREE.Color => new THREE.Color(c);
  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    fog: true,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        uTime: { value: 0 },
        uRise: { value: 0 },
        uFoamAmount: { value: options.foam === false ? 0 : 1 },
        uWaves: {
          value: [
            new THREE.Vector4(0.2, 1, 0.22, 17),
            new THREE.Vector4(-0.6, 0.8, 0.13, 9.5),
            new THREE.Vector4(0.9, 0.35, 0.07, 5.3),
            new THREE.Vector4(-0.3, -1, 0.04, 3.1),
          ].map((wave) => wave.setZ(wave.z * (options.waves ?? 1))),
        },
        uDeep: { value: linear(options.deep) },
        uShallow: { value: linear(options.shallow) },
        uSky: { value: linear(options.sky) },
        uSunDirection: { value: options.sunDirection.clone().normalize() },
        uSunColour: { value: linear(options.sunColour) },
        uFoam: { value: new THREE.Color(0.85, 0.87, 0.88) },
        uRipples: { value: null },
      },
    ]),
  });
  // Merged uniforms are clones: the texture goes in after.
  material.uniforms['uRipples']!.value = rippleNormals();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'water';
  let level = options.level;
  return {
    mesh,
    get level() {
      return level;
    },
    update(elapsed) {
      material.uniforms['uTime']!.value = elapsed;
    },
    setLevel(next) {
      level = next;
      mesh.position.y = next - options.level;
      material.uniforms['uRise']!.value = next - options.level;
    },
  };
}
